/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 28/07/2025 - 12:36:28
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 28/07/2025
    * - Author          : 
    * - Modification    : 
**/

import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Accordion,
  Button,
  Form,
  Image,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import {
  addDetectionPoints,
  deleteDetectionChecked,
  deletedetectionPointsHeight,
  removeDetectionPoint,
  setDetectionElevationCheck,
  setdetectionPointsHeight,
} from "../../../../store/reducers/bmapSlice";
import images from "../../../../images/index";
import { apis } from "../../../../api/data";
import { batchApis } from "../../../../api/batchApis";
import { MdDeleteOutline } from "react-icons/md";
import { RxCaretDown, RxCaretUp } from "react-icons/rx";
import { IoInformationCircleOutline } from "react-icons/io5";
import IconWrapper from "../../../../utils/IconWrapper";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Import Firebase
import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../../../firebase';

interface Location {
  lat: number;
  lng: number;
}

interface ResultItem {
  elevation: number;
  location: Location;
  resolution: number;
}

interface DataPoint {
  results: ResultItem[];
  status: string;
}

interface PointElevationData {
  isAutoCalculated: boolean;
  manualValue: number | null;
  apiValue: number | null;
}

interface PointPosition {
  index: number;
  lat: number;
  lng: number;
}

interface PointObject {
  lat: number;
  lng: number;
  [key: string]: any;
}

// Firestore helper functions
const migrateElevationData = async (projectId: string) => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);

    if (projectDoc.exists()) {
      const data = projectDoc.data();

      // Check if old data exists and new data doesn't
      if (data.elevationData && !data.detectionPointElevation) {
        // console.log("Migrating elevation data to new structure for project:", projectId);

        // Copy old data to new field
        await updateDoc(projectRef, {
          detectionPointElevation: data.elevationData
        });

        // Optionally, remove old data after successful migration
        // Uncomment the next line after confirming the migration works
        // await updateDoc(projectRef, { elevationData: deleteField() });

        return data.elevationData;
      }

      // If new data already exists, use that
      if (data.detectionPointElevation) {
        return data.detectionPointElevation;
      }

      // If old data exists, use that
      if (data.elevationData) {
        return data.elevationData;
      }
    }

    return {};
  } catch (error) {
    console.error("Error migrating elevation data:", error);
    return {};
  }
};

const savePointElevation = async (
  projectId: string,
  pointIndex: number,
  data: PointElevationData
) => {
  try {
    const projectRef = doc(db, 'projects', projectId);

    // Check if project document exists
    const projectDoc = await getDoc(projectRef);

    if (projectDoc.exists()) {
      // Update existing document
      await updateDoc(projectRef, {
        [`detectionPointElevation.${pointIndex}`]: data
      });
    } else {
      // Create new document if it doesn't exist
      await setDoc(projectRef, {
        detectionPointElevation: {
          [pointIndex]: data
        }
      }, { merge: true });
    }
  } catch (error) {
    console.error("Error saving elevation data:", error);
  }
};

const deletePointElevation = async (projectId: string, pointIndex: number) => {
  try {
    const projectRef = doc(db, 'projects', projectId);

    // Check if project document exists
    const projectDoc = await getDoc(projectRef);

    if (projectDoc.exists()) {
      // Delete the specific point data
      await updateDoc(projectRef, {
        [`detectionPointElevation.${pointIndex}`]: deleteField()
      });

      // Shift all higher indices down by one
      const data = projectDoc.data();
      if (data && data.detectionPointElevation) {
        const elevationData = data.detectionPointElevation;
        const updates: Record<string, any> = {};

        // Find all indices greater than pointIndex
        Object.keys(elevationData)
          .map(Number)
          .filter(idx => idx > pointIndex)
          .sort((a, b) => a - b)
          .forEach(idx => {
            // Move data from idx to idx-1
            updates[`detectionPointElevation.${idx-1}`] = elevationData[idx];
            // Mark idx for deletion
            updates[`detectionPointElevation.${idx}`] = deleteField();
          });

        // Apply all updates if there are any
        if (Object.keys(updates).length > 0) {
          await updateDoc(projectRef, updates);
        }
      }
    }
  } catch (error) {
    console.error("Error deleting elevation data:", error);
  }
};

const loadProjectElevationData = async (projectId: string) => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);

    if (projectDoc.exists()) {
      const data = projectDoc.data();

      // Check if we need to migrate
      if (data.elevationData && !data.detectionPointElevation) {
        // Perform migration
        return await migrateElevationData(projectId);
      }

      // Return new field if it exists
      return data.detectionPointElevation || {};
    }

    return {};
  } catch (error) {
    console.error("Error loading elevation data:", error);
    return {};
  }
};

const saveDetectionPointHeight = async (projectId: string, index: number, value: number | null) => {
  try {
    // console.log(`Saving detection point height for index ${index}: ${value}`);s
    const projectRef = doc(db, 'projects', projectId);

    // Get current document
    const projectDoc = await getDoc(projectRef);

    if (projectDoc.exists()) {
      const data = projectDoc.data();

      // Get current heights array or create a new one
      let heights = data.detectionPointHeight || [];

      // Make sure it's an array
      if (!Array.isArray(heights)) {
        heights = [];
      }

      // Ensure array is long enough
      while (heights.length <= index) {
        heights.push(null);
      }

      // Update the value at the specified index
      heights[index] = value;

      // Save back to Firebase
      await updateDoc(projectRef, {
        detectionPointHeight: heights
      });

      // console.log(`Successfully saved detection point height for index ${index}`);
    } else {
      // Create new document with the height array
      const heights = Array(index + 1).fill(null);
      heights[index] = value;

      await setDoc(projectRef, {
        detectionPointHeight: heights
      }, { merge: true });
    }
  } catch (error) {
    console.error(`Error saving detection point height for index ${index}:`, error);
  }
};

// 2. Now, let's add a function to handle deletion of heights when a point is deleted
const deleteDetectionPointHeight = async (projectId: string, index: number) => {
  try {
    // console.log(`Deleting detection point height at index ${index}`);
    const projectRef = doc(db, 'projects', projectId);

    // Get current document
    const projectDoc = await getDoc(projectRef);

    if (projectDoc.exists()) {
      const data = projectDoc.data();

      // Get current heights array
      let heights = data.detectionPointHeight || [];

      // Make sure it's an array
      if (!Array.isArray(heights)) {
        console.warn("detectionPointHeight is not an array, nothing to delete");
        return;
      }

      // Remove the item at the specified index
      heights.splice(index, 1);

      // Save back to Firebase
      await updateDoc(projectRef, {
        detectionPointHeight: heights
      });

      // console.log(`Successfully deleted detection point height at index ${index}`);
    }
  } catch (error) {
    console.error(`Error deleting detection point height at index ${index}:`, error);
  }
};

// Elevation input component
const ElevationInputBox: React.FC<{
  index: number;
  projectId: string;
  elevationData: Record<number, PointElevationData>;
  isAutoCalculated: boolean;
  onSaveManualValue: (index: number, value: number | null) => void;
}> = ({
  index,
  projectId,
  elevationData,
  isAutoCalculated,
  onSaveManualValue
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const { t } = useTranslation(['detectionPvArea', 'common']);
  const [isWaiting, setIsWaiting] = useState(false);

  // Update display value when elevation data changes
  useEffect(() => {
    const pointData = elevationData[index];

    let valueToShow = "";

    if (!pointData) {
      // If no data is available yet, show loading
      setIsWaiting(true);
      valueToShow = t('detectionPvArea:fields.calculating');
    } else {
      setIsWaiting(false);

      if (isAutoCalculated) {
        // Show API value for auto-calculated points
        if (pointData.apiValue !== null && pointData.apiValue !== undefined) {
          valueToShow = pointData.apiValue.toFixed(1);
        } else {
          valueToShow = t('detectionPvArea:fields.calculating');
        }
      } else {
        // Show manual value for non-auto-calculated points
        if (pointData.manualValue !== null && pointData.manualValue !== undefined) {
          valueToShow = String(pointData.manualValue);
        } else if (pointData.apiValue !== null && pointData.apiValue !== undefined) {
          valueToShow = pointData.apiValue.toFixed(1);
        } else {
          valueToShow = "";
        }
      }
    }

    setTempValue(valueToShow);
  }, [elevationData, index, isAutoCalculated, t]);

  // Save edited value
  const saveValue = () => {
    if (!isAutoCalculated && !isWaiting) {
      if (tempValue === "") {
        onSaveManualValue(index, null);
      } else if (!isNaN(parseFloat(tempValue))) {
        onSaveManualValue(index, parseFloat(tempValue));
      }
    }
    setIsEditing(false);
  };

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      saveValue();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  return (
    <div className="d-flex gap-1 ps-2">
      <div className="coolinput">
        <label htmlFor={`elevation-${index}`} className="text">
          {t('detectionPvArea:fields.groundElevation')}
        </label>

        {isEditing && !isAutoCalculated && !isWaiting ? (
          // Editable input - shown when in edit mode
          <input
            id={`elevation-${index}`}
            type="text"
            inputMode="decimal"
            autoFocus
            className="input"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={saveValue}
            onKeyDown={handleKeyDown}
          />
        ) : (
          // Display input - shown when not in edit mode
          <input
            id={`elevation-${index}`}
            type="text"
            className={isAutoCalculated || isWaiting ? "input disabledInput" : "input"}
            disabled={isAutoCalculated || isWaiting}
            value={tempValue}
            onClick={() => {
              if (!isAutoCalculated && !isWaiting) {
                setIsEditing(true);
              }
            }}
            readOnly={!isEditing}
          />
        )}
      </div>
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-${index}`}>
            <small>
              {t('detectionPvArea:tooltips.groundElevation')}
            </small>
          </Tooltip>
        }
      >
        <Button variant="none" className="mt-2 p-0">
          <IconWrapper
            Icon={IoInformationCircleOutline}
            color="#0984E3"
            size={20}
            className="mt-3"
          />
        </Button>
      </OverlayTrigger>
    </div>
  );
};

const formatCoordinate = (value: number | undefined): string => {
  if (value === undefined) return "";
  
  // Convert to string with fixed precision and then remove trailing zeros
  const fixed = value.toFixed(5);
  
  // Return the formatted value
  return fixed;
};

const DetectionPvAreaPage: React.FC<{ projectName?: string }> = ({ projectName }) => {
  const { projectId } = useParams<{ projectId: string | any }>();

  const dispatch = useDispatch();
  const [isDropdownExpanded, setIsDropdownExpanded] = useState<boolean[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [detectionPointLength, setdetectionPointLength] = useState<number>(0);
  const [elevationData, setElevationData] = useState<Record<number, PointElevationData>>({});
  const previousProjectRef = useRef<string | null>(null);
  const prevPointPositionsRef = useRef<string | null>(null);
  const { t } = useTranslation(['detectionPvArea', 'common']);

  // Get data from Redux
  const checkedElevation = useSelector(
    (state: RootState) => state.bmap.detectionElevationChecked
  );
  const detectionPoints = useSelector(
    (state: RootState) => state.bmap.detectionPoints
  );
  const detectionPointHeight = useSelector(
    (state: RootState) => state.bmap.detectionPointsHeight
  );
  const selectedAreaType = useSelector(
    (state: RootState) => state.bmap.selectedArea
  );

  // ✅ OPTIMIZED: Use batch API for elevation fetching (more efficient)
  const fetchMissingElevations = async (currentData: Record<number, PointElevationData>) => {
    if (!projectId) return;

    // Collect points that need elevation data
    const pointsToFetch: Array<{ index: number; lat: number; lng: number; isAutoCalculated: boolean }> = [];

    detectionPoints.forEach((pointArray, i) => {
      // Important: Preserve the existing auto-calculated flag from Redux or Firestore
      const isAutoCalculated = checkedElevation[i] !== undefined
        ? checkedElevation[i]
        : (currentData[i]?.isAutoCalculated !== undefined
            ? currentData[i].isAutoCalculated
            : true);

      // Skip if we already have API data and the auto-calculate flag is correct
      if (currentData[i]?.apiValue !== undefined && currentData[i]?.apiValue !== null &&
          currentData[i].isAutoCalculated === isAutoCalculated) {
        return;
      }

      // Handle nested array structure
      const point = Array.isArray(pointArray) ? pointArray[0] : pointArray;

      if (!point || !point.lat || !point.lng) {
        console.warn(`Skipping fetch for point ${i}: Invalid coordinates`);
        return;
      }

      pointsToFetch.push({
        index: i,
        lat: point.lat,
        lng: point.lng,
        isAutoCalculated
      });
    });

    if (pointsToFetch.length === 0) return;

    try {
      // ✅ Use batch API - single call for all points
      const points = pointsToFetch.map(p => ({ lat: p.lat, lng: p.lng }));
      const res = await batchApis.getBatchElevation(points);

      if (res.data?.elevations && Array.isArray(res.data.elevations)) {
        const elevations = res.data.elevations;

        // Process each elevation result
        for (let i = 0; i < pointsToFetch.length; i++) {
          const pointInfo = pointsToFetch[i];
          const elevation = elevations[i];

          if (elevation !== null && elevation !== undefined) {
            // Get current state for this point
            const currentPointData = currentData[pointInfo.index] || {
              isAutoCalculated: pointInfo.isAutoCalculated,
              manualValue: null,
              apiValue: null
            };

            // Create the updated data
            const updatedData = {
              ...currentPointData,
              isAutoCalculated: pointInfo.isAutoCalculated,
              apiValue: elevation,
              // If not auto-calculated, initialize manual value
              manualValue: (!pointInfo.isAutoCalculated && currentPointData.manualValue === null)
                ? elevation
                : currentPointData.manualValue
            };

            // Update Firestore
            await savePointElevation(projectId, pointInfo.index, updatedData);

            // Update local state
            setElevationData(prev => ({
              ...prev,
              [pointInfo.index]: updatedData
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching batch elevations for detection points:', error);
    }
  };

  // Load data when project changes
  useEffect(() => {
    if (!projectId || previousProjectRef.current === projectId) return;

    // console.log("Loading project data for ID:", projectId);
    setIsLoading(true);
    previousProjectRef.current = projectId;

    const loadData = async () => {
      try {
        // Load elevation data from Firestore
        const projectData = await loadProjectElevationData(projectId);
        // console.log("Loaded project elevation data:", projectData);

        // First load the project document to get detection points and heights
        const projectRef = doc(db, "projects", projectId);
        const projectDoc = await getDoc(projectRef);

        if (projectDoc.exists()) {
          const data = projectDoc.data();

          // Check if we need to load detection points from the document
          if (data.detectionPoint && detectionPoints.length === 0) {
            try {
              const pointsData = JSON.parse(data.detectionPoint);
              // console.log("Loaded detection points from database:", pointsData);

              // Dispatch the points to Redux
              if (Array.isArray(pointsData) && pointsData.length > 0) {
                pointsData.forEach((value, idx) => {
                  if (value) {
                    // console.log(`Adding detection point ${idx} to Redux:`, value);
                    dispatch(addDetectionPoints({
                      index: idx,
                      detectionPoints: value
                    }));
                  }
                });
              }
            } catch (error) {
              console.error("Error parsing detection points:", error);
            }
          }

          // Load detection point heights
          if (data.detectionPointHeight && Array.isArray(data.detectionPointHeight)) {
            data.detectionPointHeight.forEach((value, idx) => {
              if (value !== undefined && value !== null) {
                // console.log(`Setting height for point ${idx}: ${value}`);
                dispatch(setdetectionPointsHeight({
                  index: idx,
                  value
                }));
              }
            });
          }
        }

        // First update local state
        setElevationData(projectData);

        // Prepare expanded state - open the last point by default
        const newExpandedState = Array(detectionPoints.length).fill(false);
        if (detectionPoints.length > 0) {
          newExpandedState[detectionPoints.length - 1] = true;
        }
        setIsDropdownExpanded(newExpandedState);

        // Then update Redux state for auto-calculate checkboxes
        for (let i = 0; i < detectionPoints.length; i++) {
          const pointData = projectData[i];
          // Use the saved state from Firestore if available
          const isAutoCalculated = pointData?.isAutoCalculated !== undefined
            ? pointData.isAutoCalculated
            : true;

          // Always update Redux to match Firestore
          dispatch(setDetectionElevationCheck({
            index: i,
            check: isAutoCalculated
          }));
        }

        // Update the point length counter
        setdetectionPointLength(detectionPoints.length);

        // We're done loading
        setIsLoading(false);

        // Fetch missing elevation data after state is updated and loading is complete
        setTimeout(() => {
          // console.log("Fetching missing elevations after load");
          fetchMissingElevations(projectData);
        }, 300);
      } catch (error) {
        console.error("Error loading project data:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [projectId, detectionPoints.length, dispatch]);

  // Save manual elevation value
  const saveManualValue = useCallback(async (index: number, value: number | null) => {
    if (!projectId) return;

    try {
      const currentPointData = elevationData[index] || {
        isAutoCalculated: false,
        manualValue: null,
        apiValue: null
      };

      // Update Firestore
      await savePointElevation(projectId, index, {
        ...currentPointData,
        isAutoCalculated: false,
        manualValue: value
      });

      // Update local state
      setElevationData(prev => ({
        ...prev,
        [index]: {
          ...currentPointData,
          isAutoCalculated: false,
          manualValue: value
        }
      }));
    } catch (error) {
      console.error("Error saving manual value:", error);
    }
  }, [elevationData, projectId]);

  // Handle auto-calculate checkbox change
  const handleAutoCalculateChange = async (index: number, isChecked: boolean) => {
    if (!projectId) return;

    try {
      // console.log(`Changing auto-calculate for point ${index} to ${isChecked}`);

      // Get current point data
      const currentPointData = elevationData[index] || {
        isAutoCalculated: true,
        manualValue: null,
        apiValue: null
      };

      // If turning off auto-calculate, initialize manual value with API value
      const manualValue = !isChecked && currentPointData.apiValue !== null && currentPointData.manualValue === null
        ? currentPointData.apiValue
        : currentPointData.manualValue;

      // Create updated data object
      const updatedData = {
        ...currentPointData,
        isAutoCalculated: isChecked,
        manualValue
      };

      // Update Firebase
      await savePointElevation(projectId, index, updatedData);

      // Update local state
      setElevationData(prev => ({
        ...prev,
        [index]: updatedData
      }));

      // Update Redux state
      dispatch(setDetectionElevationCheck({
        index,
        check: isChecked
      }));

      // If turning ON auto-calculate, we need to fetch the updated elevation
      if (isChecked) {
        // Extract the point coordinates with proper type handling
        let pointLat: number | undefined;
        let pointLng: number | undefined;

        // Handle different potential structures of detectionPoints
        if (Array.isArray(detectionPoints[index])) {
          // If it's an array like [[{lat, lng}]]
          const pointArray = detectionPoints[index] as any[];
          if (pointArray.length > 0 && pointArray[0]) {
            const point = pointArray[0];
            pointLat = typeof point.lat === 'number' ? point.lat : undefined;
            pointLng = typeof point.lng === 'number' ? point.lng : undefined;
          }
        } else {
          // If it's a direct object like {lat, lng}
          const point = detectionPoints[index] as any;
          if (point) {
            pointLat = typeof point.lat === 'number' ? point.lat : undefined;
            pointLng = typeof point.lng === 'number' ? point.lng : undefined;
          }
        }

        // Only proceed if we have valid coordinates
        if (pointLat !== undefined && pointLng !== undefined) {
          try {
            // console.log(`Fetching elevation for point ${index} at (${pointLat}, ${pointLng})`);

            // Show loading state while fetching
            setElevationData(prev => ({
              ...prev,
              [index]: {
                ...prev[index],
                isAutoCalculated: true,
                apiValue: null // Clear API value to show loading
              }
            }));

            // ✅ Use batch API even for single point (consistent with other components)
            const res = await batchApis.getBatchElevation([{ lat: pointLat, lng: pointLng }]);

            if (res.data?.elevations && res.data.elevations.length > 0) {
              const elevation = res.data.elevations[0];

              if (elevation !== null && elevation !== undefined) {
                // Update with new elevation value
                const newData = {
                  ...updatedData,
                  apiValue: elevation
                };

                // Update Firebase
                await savePointElevation(projectId, index, newData);

                // Update local state
                setElevationData(prev => ({
                  ...prev,
                  [index]: newData
                }));
              }
            }
          } catch (error) {
            console.error(`Error fetching elevation for point ${index}:`, error);
          }
        } else {
          // console.warn(`Cannot fetch elevation for point ${index}: Invalid coordinates`);
        }
      }
    } catch (error) {
      console.error("Error updating auto-calculate:", error);
    }
  };

  // TypeScript-safe point movement handler with explicit type assertions
const handlePointMovement = useCallback(async () => {
  if (!projectId || isLoading) return;

  // Store current point positions to detect changes
  const currentPoints: PointPosition[] = [];

  // Extract points with proper type checking and explicit type assertions
  detectionPoints.forEach((point, index) => {
    try {
      // Handle array structure like [[{lat, lng}]]
      if (Array.isArray(point) && point.length > 0) {
        const pointObj = point[0] as any;
        if (pointObj &&
            typeof pointObj === 'object' &&
            'lat' in pointObj &&
            'lng' in pointObj) {
          // Use explicit type assertion
          const typedPoint = pointObj as PointObject;
          if (typeof typedPoint.lat === 'number' && typeof typedPoint.lng === 'number') {
            currentPoints.push({
              index,
              lat: typedPoint.lat,
              lng: typedPoint.lng
            });
          }
        }
      }
      // Handle direct object like {lat, lng}
      else if (point && typeof point === 'object') {
        // Use explicit type assertion
        const typedPoint = point as any;
        if ('lat' in typedPoint && 'lng' in typedPoint &&
            typeof typedPoint.lat === 'number' && typeof typedPoint.lng === 'number') {
          currentPoints.push({
            index,
            lat: typedPoint.lat,
            lng: typedPoint.lng
          });
        }
      }
    } catch (error) {
      console.warn(`Error processing point at index ${index}:`, error);
    }
  });

  // Convert to JSON string for comparison
  const currentPointsJSON = JSON.stringify(currentPoints);

  // Compare with previous positions
  if (prevPointPositionsRef.current !== currentPointsJSON) {
    // console.log("Detection point movement detected");

    // Update previous positions reference
    prevPointPositionsRef.current = currentPointsJSON;

    // IMPORTANT NEW CODE: Update all detection point coordinates in Firebase
    try {
      // Get reference to the project document
      const projectRef = doc(db, "projects", projectId);
      const projectDoc = await getDoc(projectRef);

      if (projectDoc.exists()) {
        // Define explicit type for allPoints to avoid TypeScript error
        type DetectionPointArray = Array<Array<{lat: number, lng: number}> | null>;
        
        // Get existing detection points or create empty array
        let allPoints: DetectionPointArray = [];
        try {
          const projectData = projectDoc.data();
          if (projectData.detectionPoint) {
            const parsedPoints = JSON.parse(projectData.detectionPoint);
            // Ensure the parsed data is an array
            allPoints = Array.isArray(parsedPoints) ? parsedPoints : [];
          }
        } catch (e) {
          console.error("Error parsing detection points:", e);
          allPoints = [];
        }

        // Ensure allPoints is long enough
        while (allPoints.length < detectionPoints.length) {
          allPoints.push(null);
        }

        // Update all points with their current coordinates
        let hasChanges = false;
        currentPoints.forEach(({index, lat, lng}) => {
          // Create point object with the current coordinates
          const updatedPoint = { lat, lng };
          
          // Check if this point needs updating
          const currentPointStr = JSON.stringify([updatedPoint]);
          const existingPointStr = JSON.stringify(allPoints[index]);
          
          if (currentPointStr !== existingPointStr) {
            allPoints[index] = [updatedPoint];
            hasChanges = true;
          }
        });

        // Only update Firebase if coordinates have changed
        if (hasChanges) {
          // console.log("Updating detection point coordinates in Firebase");
          await updateDoc(projectRef, {
            detectionPoint: JSON.stringify(allPoints)
          });
        }
      }
    } catch (error) {
      console.error("Error updating detection point coordinates:", error);
    }

    // ✅ OPTIMIZED: Collect all points that need elevation update and use batch API
    const pointsToFetch: Array<{ index: number; lat: number; lng: number; pointElevationData: PointElevationData }> = [];

    for (const pointData of currentPoints) {
      const { index, lat, lng } = pointData;

      // Skip if auto-calculate is not enabled for this point
      const pointElevationData = elevationData[index];
      if (!pointElevationData || !pointElevationData.isAutoCalculated) {
        continue;
      }

      pointsToFetch.push({ index, lat, lng, pointElevationData });

      // Show loading state
      setElevationData(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          apiValue: null // Clear API value to show loading
        }
      }));
    }

    if (pointsToFetch.length > 0) {
      try {
        // ✅ Use batch API - single call for all moved points
        const points = pointsToFetch.map(p => ({ lat: p.lat, lng: p.lng }));
        const res = await batchApis.getBatchElevation(points);

        if (res.data?.elevations && Array.isArray(res.data.elevations)) {
          const elevations = res.data.elevations;

          // Process each elevation result
          for (let i = 0; i < pointsToFetch.length; i++) {
            const pointInfo = pointsToFetch[i];
            const elevation = elevations[i];

            if (elevation !== null && elevation !== undefined) {
              // Update data
              const updatedData: PointElevationData = {
                ...pointInfo.pointElevationData,
                apiValue: elevation
              };

              // Update Firebase
              await savePointElevation(projectId, pointInfo.index, updatedData);

              // Update local state
              setElevationData(prev => ({
                ...prev,
                [pointInfo.index]: updatedData
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error updating batch elevations for moved points:', error);
      }
    }
  }
}, [detectionPoints, projectId, isLoading, elevationData]);

  useEffect(() => {
    // Skip during initial loading
    if (isLoading) return;

    // Set a small delay to allow for multiple movements in a short time
    const timer = setTimeout(() => {
      handlePointMovement();
    }, 500);

    return () => clearTimeout(timer);
  }, [detectionPoints, handlePointMovement, isLoading]);

  useEffect(() => {
    // Skip if loading or no project ID
    if (isLoading || !projectId) return;

    // console.log(`Detection points length changed: ${detectionPoints.length}, previous: ${detectionPointLength}`);

    const handleDetectionPointsChange = async () => {
      // Case 1: New points added
      if (detectionPoints.length > detectionPointLength) {
        // Expand only the newest point
        setIsDropdownExpanded(prev => {
          const newState = Array(detectionPoints.length).fill(false);
          newState[detectionPoints.length - 1] = true;
          return newState;
        });

        // Process all points that might be new (from detectionPointLength to end)
        for (let i = detectionPointLength; i < detectionPoints.length; i++) {
          // console.log(`Processing new point at index ${i}`);

          // Check if this index already exists in Firestore
          const existingPointData = elevationData[i];
          const isAutoCalculated = existingPointData?.isAutoCalculated !== undefined
            ? existingPointData.isAutoCalculated
            : true; // Default to true if not found

          // Update Redux state for the checkbox
          dispatch(setDetectionElevationCheck({
            index: i,
            check: isAutoCalculated
          }));

          // Save point coordinates to the database
          try {
            // Get the coordinates for this point
            const pointData = detectionPoints[i];
            const point = Array.isArray(pointData) ? pointData[0] : pointData;

            if (point) {
              // console.log(`Saving detection point ${i} to database:`, point);

              // Create a document reference
              const projectRef = doc(db, "projects", projectId);

              // Get the current document
              const projectDoc = await getDoc(projectRef);

              if (projectDoc.exists()) {
                // Parse existing detection points or use empty array
                let allPoints = [];
                try {
                  allPoints = projectDoc.data().detectionPoint
                    ? JSON.parse(projectDoc.data().detectionPoint)
                    : [];
                } catch (e) {
                  console.error("Error parsing detection points:", e);
                  allPoints = [];
                }

                // Ensure allPoints is an array of the correct length
                while (allPoints.length < detectionPoints.length) {
                  allPoints.push(null);
                }

                // Update with the new point
                allPoints[i] = [point];

                // Save back to database
                await updateDoc(projectRef, {
                  detectionPoint: JSON.stringify(allPoints)
                });

                // console.log(`Successfully saved detection point ${i} to database`);
              }
            }
          } catch (e) {
            console.error(`Error saving detection point ${i} to database:`, e);
          }
        }

        // Update the point length after processing
        setdetectionPointLength(detectionPoints.length);
      }
      // Case 2: Points removed (reset or delete)
      else if (detectionPoints.length < detectionPointLength) {
        // console.log("Detection points were removed, updating state");
        setdetectionPointLength(detectionPoints.length);

        // Adjust dropdown expanded state
        setIsDropdownExpanded(prev => {
          const newState = Array(detectionPoints.length).fill(false);
          if (detectionPoints.length > 0) {
            newState[detectionPoints.length - 1] = true;
          }
          return newState;
        });
      }

      // After handling adds/removes, ensure all points have elevation data
      // console.log("Checking for missing elevation data");
      await fetchMissingElevations(elevationData);
    };

    // Execute the function
    handleDetectionPointsChange();

  }, [detectionPoints.length, isLoading, detectionPointLength, projectId, dispatch, elevationData]);

  const handleHeightChange = useCallback((index: number, value: number | null) => {
    if (isLoading) return;

    // Update Redux state
    dispatch(
      setdetectionPointsHeight({
        index,
        value,
      })
    );

    // Save to Firebase if we have a project ID
    if (projectId) {
      saveDetectionPointHeight(projectId, index, value);
    }
  }, [isLoading, dispatch, projectId]);

  // Handle deletion of detection point
  const handleDeleteDetectionPoint = async (index: number) => {
    if (isLoading || !projectId) return;

    try {
      // console.log(`Deleting detection point at index ${index}`);

      // Get a reference to the project document
      const projectRef = doc(db, "projects", projectId);
      const projectDoc = await getDoc(projectRef);

      if (projectDoc.exists()) {
        // Get and parse the current detection points
        let allPoints = [];
        try {
          allPoints = projectDoc.data().detectionPoint
            ? JSON.parse(projectDoc.data().detectionPoint)
            : [];
        } catch (e) {
          console.error("Error parsing detection points:", e);
          allPoints = [];
        }

        // Remove the point at the specified index
        allPoints.splice(index, 1);

        // Save the updated points array back to the database
        await updateDoc(projectRef, {
          detectionPoint: JSON.stringify(allPoints)
        });

        // Delete the height value
        await deleteDetectionPointHeight(projectId, index);

        // console.log("Saved updated detection points to database");
      }

      // Update Redux state
      dispatch(removeDetectionPoint(index));
      dispatch(deletedetectionPointsHeight({ index }));
      dispatch(deleteDetectionChecked({ index }));

      // Delete from Firestore and shift indices
      await deletePointElevation(projectId, index);

      // Update local state
      setElevationData(prev => {
        const newData: Record<number, PointElevationData> = {};

        // Shift all indices
        Object.keys(prev).forEach(key => {
          const keyNum = parseInt(key);
          if (keyNum < index) {
            newData[keyNum] = prev[keyNum];
          } else if (keyNum > index) {
            newData[keyNum - 1] = prev[keyNum];
          }
        });

        return newData;
      });

      // Update dropdown expanded state
      setIsDropdownExpanded(prev => {
        const newState = [...prev];
        newState.splice(index, 1);
        return newState;
      });

      // Update point length
      setdetectionPointLength(prev => prev - 1);

    } catch (error) {
      console.error("Error deleting detection point:", error);
    }
  };

  // Toggle dropdown expanded state
  const toggleDropdown = useCallback((index: number) => {
    if (isLoading) return;

    setIsDropdownExpanded(prev => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  }, [isLoading]);

  // Handle new detection points
  useEffect(() => {
    if (isLoading || !projectId) return;

    if (detectionPoints.length > 0 && detectionPoints.length > detectionPointLength) {
      // Expand only the newest point
      setIsDropdownExpanded(prev => {
        const newState = Array(detectionPoints.length).fill(false);
        newState[detectionPoints.length - 1] = true;
        return newState;
      });

      setdetectionPointLength(detectionPoints.length);

      // Set default auto-calculate for new point
      const newIndex = detectionPoints.length - 1;

      // Check if this index already exists in Firestore
      const existingPointData = elevationData[newIndex];
      const isAutoCalculated = existingPointData?.isAutoCalculated !== undefined
        ? existingPointData.isAutoCalculated
        : true; // Default to true if not found

      // Save new point with proper settings to Firestore
      savePointElevation(projectId, newIndex, {
        isAutoCalculated: isAutoCalculated,
        manualValue: existingPointData?.manualValue || null,
        apiValue: existingPointData?.apiValue || null
      });

      // Update Redux state
      dispatch(setDetectionElevationCheck({
        index: newIndex,
        check: isAutoCalculated
      }));

      // Fetch elevation for the new point, passing current data
      fetchMissingElevations(elevationData);
    }
  }, [detectionPoints.length, isLoading, detectionPointLength, projectId, dispatch, elevationData]);

  // Render auto-calculate checkbox
  const renderCheckboxSection = useCallback((index: number) => {
    const pointData = elevationData[index] || {
      isAutoCalculated: true,
      manualValue: null,
      apiValue: null
    };

    return (
      <div className="d-flex align-items-center ps-2">
        <Form.Check
          id={`auto-calculate-${index}`}
          checked={pointData.isAutoCalculated}
          onChange={(e) => handleAutoCalculateChange(index, e.target.checked)}
        />
        <label
          className="ms-2"
          htmlFor={`auto-calculate-${index}`}
          style={{ fontSize: "12px", color: "#858585" }}
        >
          {t('detectionPvArea:fields.autoCalculate')}
        </label>
      </div>
    );
  }, [elevationData, handleAutoCalculateChange, t]);

  // Render elevation input
  const renderElevationSection = useCallback((index: number) => {
    const pointData = elevationData[index] || {
      isAutoCalculated: true,
      manualValue: null,
      apiValue: null
    };

    return (
      <ElevationInputBox
        index={index}
        projectId={projectId || ''}
        elevationData={elevationData}
        isAutoCalculated={pointData.isAutoCalculated}
        onSaveManualValue={saveManualValue}
      />
    );
  }, [elevationData, projectId, saveManualValue]);

  // If projectId is missing, show warning
  if (!projectId) {
    return (
      <div className="text-center mt-5">
        <h4>Error: Project ID is missing</h4>
        <p>Please make sure to open this page from the dashboard.</p>
      </div>
    );
  }

  // Main render
return (
    <div>
      {detectionPoints.length < 1 &&
        (selectedAreaType?.value === "detection" ||
          selectedAreaType?.value === "detectionTab") && (
          <div className="detectionNothingToShow">
            <div>
              <Image
                src={images.AddDetectionPoint}
                alt="addTiltedPVarea"
                loading="lazy"
              />
              <strong>{t('detectionPvArea:emptyState.title')}</strong>
              <small>
                {t('detectionPvArea:emptyState.description')}
              </small>
            </div>
          </div>
        )}
      {detectionPoints.length > 0 &&
        (selectedAreaType?.value === "detection" ||
          selectedAreaType?.value === "detectionTab") && (
          <div style={{ height: "76vh", overflowY: "auto" }}>
            {detectionPoints.map((value: any, index: number) => (
              <div
                className="DetectionPvAreaPage mb-2 w-100"
                key={`detectionPoints${index}`}
              >
                <Accordion
                  className="w-100"
                  activeKey={isDropdownExpanded[index] ? `${index}` : null}
                >
                  <Accordion.Item
                    eventKey={`${index}`}
                    style={{ width: "100%" }}
                  >
                    <Accordion.Header
                      onClick={() => toggleDropdown(index)}
                      style={{ width: "100%" }}
                    >
                      <div className="projectName">
                        {`${t('detectionPvArea:card.prefix')} - ${index + 1}`} &nbsp; |
                        <IconWrapper Icon={MdDeleteOutline} size={17}
                          className="mb-1"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent accordion from toggling
                            handleDeleteDetectionPoint(index);
                          }}/>
                      </div>
                      <div className={`collapseBtn`}>
                        {isDropdownExpanded[index] ? (
                          <span>
                            {t('detectionPvArea:card.collapse')} <IconWrapper Icon={RxCaretUp} size={20}/>
                          </span>
                        ) : (
                          <span>
                            {t('detectionPvArea:card.expand')} <IconWrapper Icon={RxCaretDown} size={20}/>
                          </span>
                        )}
                      </div>
                    </Accordion.Header>
                    
                    <Accordion.Body style={{ width: "100%" }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <p className="m-0">{t('detectionPvArea:sections.altitudeParameters')}</p>
                      </div>
                      <div className="d-flex justify-content-between mt-2">
                        <div className="coolinput">
                          <label htmlFor={`latitude-${index}`} className="text">
                            {t('detectionPvArea:fields.latitude')}
                          </label>
                          <input
                            id={`latitude-${index}`}
                            type="text"
                            className="input disabledInput"
                            disabled
                            value={
                              Array.isArray(value) && value.length > 0 && value[0].lat 
                                ? formatCoordinate(value[0].lat) 
                                : ""
                            }
                          />
                        </div>
                        <div className="coolinput" style={{marginRight:"28px"}}>
                          <label htmlFor={`longitude-${index}`} className="text">
                            {t('detectionPvArea:fields.longitude')}
                          </label>
                          <input
                            id={`longitude-${index}`}
                            type="text"
                            className="input disabledInput"
                            disabled
                            value={
                              Array.isArray(value) && value.length > 0 && value[0].lng 
                                ? formatCoordinate(value[0].lng) 
                                : ""
                            }
                          />
                        </div>
                      </div>
                      <div className="d-flex justify-content-between">
                        <div className="coolinput">
                          <label htmlFor="input" className="text">
                            {t('detectionPvArea:fields.aboveGround')}
                          </label>
                          <input
                            id={`${index}`}
                            type="number"
                            step="0.01"
                            placeholder={t('detectionPvArea:fields.groundLevelPlaceholder')}
                            name="input"
                            className="input"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const nextIndex =
                                  index === detectionPoints.length - 1
                                    ? 0
                                    : index + 1;
                                const nextInput = document.getElementById(
                                  `${nextIndex}`
                                );

                                if (!isDropdownExpanded[nextIndex]) {
                                  toggleDropdown(nextIndex);
                                  setTimeout(() => {
                                    nextInput?.focus();
                                  }, 300);
                                } else {
                                  nextInput?.focus();
                                }
                              }
                            }}
                            value={detectionPointHeight[index] || ""}
                            onChange={(e) => {
                              if (isLoading) return;

                              const valueStr = e.target.value;

                              if (valueStr === "") {
                                // Handle empty input explicitly as null
                                handleHeightChange(index, null);
                              } else {
                                // Only parse and pass the value if it's not empty
                                const numValue = parseFloat(valueStr);
                                if (!isNaN(numValue)) {
                                  handleHeightChange(index, numValue);
                                }
                              }
                            }}
                          />
                        </div>
                        <div className="d-flex flex-column">
                          {renderElevationSection(index)}
                          {renderCheckboxSection(index)}
                        </div>
                      </div>
  
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default DetectionPvAreaPage;
